import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { LoadingOverlay, PagePaper } from 'dashboard/components/common';

/**
 * Brand mail config — sender identity + optional SendGrid override.
 * The API key never echoes back; we only show whether one is stored.
 */
export default function ConfigTab({ brandId }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    if (!brandId) return Promise.resolve();
    setLoading(true);
    return API.getBrandMailConfig({ brand_id: brandId })
      .then((res) => {
        setConfig(res);
        setForm({
          from_email: res?.from_email || '',
          from_name: res?.from_name || '',
          reply_to: res?.reply_to || '',
          use_own_sendgrid: !!res?.use_own_sendgrid,
          sendgrid_api_key: '',
        });
      })
      .catch((err) => toast.error(err?.message || 'Failed to load config', { toastId: 'mail-config-load-error' }))
      .finally(() => setLoading(false));
  }, [brandId]);

  useEffect(() => {
    if (!brandId) return undefined;
    let cancelled = false;
    setLoading(true);
    API.getBrandMailConfig({ brand_id: brandId })
      .then((res) => {
        if (cancelled) return;
        setConfig(res);
        setForm({
          from_email: res?.from_email || '',
          from_name: res?.from_name || '',
          reply_to: res?.reply_to || '',
          use_own_sendgrid: !!res?.use_own_sendgrid,
          sendgrid_api_key: '',
        });
      })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load config', { toastId: 'mail-config-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [brandId]);

  const save = () => {
    setSaving(true);
    const patch = {
      from_email: form.from_email,
      from_name: form.from_name,
      reply_to: form.reply_to,
      use_own_sendgrid: form.use_own_sendgrid,
    };
    if (form.sendgrid_api_key) patch.sendgrid_api_key = form.sendgrid_api_key;
    API.upsertBrandMailConfig({ brand_id: brandId, patch })
      .then(() => { toast.success('Saved'); reload(); })
      .catch((err) => toast.error(err?.message || 'Failed to save'))
      .finally(() => setSaving(false));
  };

  const clearKey = () => {
    if (!window.confirm('Remove the stored SendGrid key?')) return;
    API.upsertBrandMailConfig({ brand_id: brandId, patch: { clear_api_key: true } })
      .then(() => { toast.success('Key removed'); reload(); })
      .catch((err) => toast.error(err?.message || 'Failed to clear'));
  };

  return (
    <PagePaper>
      <LoadingOverlay loading={loading} minHeight={200}>
        <Stack spacing='14px'>
          <Box>
            <Typography variant='h6' fontWeight='700' mb='4px'>Sender identity</Typography>
            <Typography variant='body2' color='text.secondary'>
              How emails sent on behalf of this brand will appear.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='12px'>
            <TextField
              size='small'
              label='From email'
              placeholder='hello@yourbrand.com'
              value={form.from_email || ''}
              onChange={(e) => setForm({ ...form, from_email: e.target.value })}
              sx={{ flex: 2 }}
            />
            <TextField
              size='small'
              label='From name'
              placeholder='Your Brand'
              value={form.from_name || ''}
              onChange={(e) => setForm({ ...form, from_name: e.target.value })}
              sx={{ flex: 2 }}
            />
            <TextField
              size='small'
              label='Reply-to'
              placeholder='support@yourbrand.com'
              value={form.reply_to || ''}
              onChange={(e) => setForm({ ...form, reply_to: e.target.value })}
              sx={{ flex: 2 }}
            />
          </Stack>

          <Divider />

          <Box>
            <Typography variant='h6' fontWeight='700' mb='4px'>SendGrid (optional)</Typography>
            <Typography variant='body2' color='text.secondary'>
              Use your own SendGrid account so deliverability and analytics roll up under your domain.
              Leave blank to use the platform's sender.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing='12px' alignItems='flex-end'>
            <TextField
              size='small'
              type='password'
              label='API key'
              placeholder={config?.has_api_key ? `Stored — ${config.redacted_api_key}` : 'SG.…'}
              value={form.sendgrid_api_key || ''}
              onChange={(e) => setForm({ ...form, sendgrid_api_key: e.target.value })}
              fullWidth
              helperText='Stored encrypted with AES-256-GCM. Never echoed back to the browser.'
              sx={{ flex: 2 }}
            />
            {config?.has_api_key && (
              <Button variant='outlined' color='error' onClick={clearKey}>
                Remove key
              </Button>
            )}
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={!!form.use_own_sendgrid}
                onChange={(e) => setForm({ ...form, use_own_sendgrid: e.target.checked })}
              />
            }
            label="Use this brand's SendGrid key for outbound mail"
          />

          <Stack direction='row' justifyContent='flex-end' pt='8px'>
            <Button variant='contained' onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </LoadingOverlay>
    </PagePaper>
  );
}
